import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

interface AccordionContextValue {
  expandedKeys: Set<string>;
  toggleItem: (key: string) => void;
  selectionMode: "single" | "multiple" | "none";
}

const AccordionContext = createContext<AccordionContextValue | undefined>(
  undefined
);

const useAccordionContext = (): AccordionContextValue => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error("AccordionItem must be used within Accordion");
  }
  return context;
};

export interface AccordionItemProps {
  itemKey?: string;
  title?: ReactNode;
  children: ReactNode;
  classNames?: {
    base?: string;
    trigger?: string;
    content?: string;
  };
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  itemKey,
  title,
  children,
  classNames,
}) => {
  const { expandedKeys, toggleItem, selectionMode } = useAccordionContext();
  const isOpen = itemKey ? expandedKeys.has(itemKey) : false;
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    if (contentRef.current) {
      if (isOpen) {
        // Set height to scrollHeight for smooth expand
        setContentHeight(contentRef.current.scrollHeight);
      } else {
        // Set height to 0 for smooth collapse
        setContentHeight(0);
      }
    }
  }, [isOpen]);

  // Update height when content changes
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children, isOpen]);

  const handleToggle = (): void => {
    if (itemKey && selectionMode !== "none") {
      toggleItem(itemKey);
    }
  };

  return (
    <div
      key={itemKey}
      className={cn(
        !classNames?.base && "border-b border-gray-200",
        classNames?.base
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          "w-full flex items-center justify-between py-2 text-left",
          isOpen && "text-primary rounded-b-none",
          !isOpen && "rounded-b-2xl",
          classNames?.trigger
        )}
      >
        <div className="flex-1">{title}</div>
        <svg
          className={cn(
            "w-4 h-4 transition-transform duration-300 ease-in-out shrink-0",
            isOpen && "rotate-180"
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          classNames?.content
        )}
        style={{
          maxHeight: `${contentHeight}px`,
        }}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
};

export interface AccordionProps {
  selectionMode?: "single" | "multiple" | "none";
  className?: string;
  defaultExpandedKeys?: string[];
  children: ReactNode;
}

const Accordion: React.FC<AccordionProps> = ({
  selectionMode = "multiple",
  className,
  defaultExpandedKeys = [],
  children,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    new Set(defaultExpandedKeys)
  );
  const prevDefaultKeysRef = useRef<string>("");

  // Memoize the string representation of defaultExpandedKeys for comparison
  const defaultKeysString = useMemo(
    () => defaultExpandedKeys.sort().join(","),
    [defaultExpandedKeys]
  );

  useEffect(() => {
    // Only update if defaultExpandedKeys actually changed
    if (prevDefaultKeysRef.current !== defaultKeysString) {
      prevDefaultKeysRef.current = defaultKeysString;
      if (defaultExpandedKeys.length > 0) {
        setExpandedKeys(new Set(defaultExpandedKeys));
      }
    }
  }, [defaultExpandedKeys, defaultKeysString]);

  const toggleItem = (key: string): void => {
    setExpandedKeys((prev) => {
      const newSet = new Set(prev);
      if (selectionMode === "single") {
        // Close all others and toggle this one
        if (newSet.has(key)) {
          // Don't allow closing if it's the only one open
          if (newSet.size === 1) {
            return prev; // Keep it open
          }
          newSet.clear();
        } else {
          newSet.clear();
          newSet.add(key);
        }
      } else {
        // Multiple mode: toggle this item
        if (newSet.has(key)) {
          // Don't allow closing if it's the only one open
          if (newSet.size === 1) {
            return prev; // Keep it open
          }
          newSet.delete(key);
        } else {
          newSet.add(key);
        }
      }
      return newSet;
    });
  };

  return (
    <AccordionContext.Provider
      value={{ expandedKeys, toggleItem, selectionMode }}
    >
      <div className={cn("w-full", className)}>{children}</div>
    </AccordionContext.Provider>
  );
};

export { Accordion, AccordionItem };
